// src/contexts/DarkModeContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'C2_darkMode';

interface DarkModeContextValue {
  isDarkMode: boolean;
  setDarkMode: (enabled: boolean) => void;
}

const DarkModeContext = createContext<DarkModeContextValue>({
  isDarkMode: false,
  setDarkMode: () => {},
});

const applyDarkMode = (enabled: boolean) =>
{
  const html = document.documentElement;
  if (enabled)
  {
    html.classList.add('dark', 'dark-mode');
  }
  else
  {
    html.classList.remove('dark', 'dark-mode');
  }
};

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) =>
{
  const [isDarkMode, setIsDarkMode] = useState(() =>
  {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  // Apply on mount and whenever it changes
  useEffect(() =>
  {
    applyDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Listen for storage changes from other tabs
  useEffect(() =>
  {
    const handler = (e: StorageEvent) =>
    {
      if (e.key === STORAGE_KEY)
      {
        const enabled = e.newValue === 'true';
        setIsDarkMode(enabled);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setDarkMode = useCallback((enabled: boolean) =>
  {
    setIsDarkMode(enabled);
    localStorage.setItem(STORAGE_KEY, String(enabled));
    applyDarkMode(enabled);
  }, []);

  return (
    <DarkModeContext.Provider value={{ isDarkMode, setDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => useContext(DarkModeContext);
